import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaBuilding,
  FaChartLine,
  FaCheck,
  FaCreditCard,
  FaReceipt,
  FaWallet,
} from 'react-icons/fa';
import { FaChartSimple, FaSackDollar, FaTriangleExclamation } from 'react-icons/fa6';

import { HiUserGroup } from 'react-icons/hi';
import PageHeader from '../../components/misc/PageHeader';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import * as bankAccountService from '../../../services/bankAccountService';
import { BalanceSheet } from '../../../types/bankAccount';
import { formatEuroCents, formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';

// GL6572120000072123
interface FinancialBalancePageProps {
  hideHeader?: boolean;
}

export default function FinancialBalancePage({
  hideHeader = false,
}: FinancialBalancePageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.balance' });
  const business = useSelectedBusiness();
  const [balanceSheet, setBalanceSheet] = React.useState<BalanceSheet>();

  useEffect(() => {
    bankAccountService.getBalanceStatement().then(setBalanceSheet);
  }, [business]);

  const locale = i18n.language;

  if (!balanceSheet) {
    return <div>Loading...</div>;
  }

  const isBalanced =
    Math.abs(
      balanceSheet.assets.totalAssets - balanceSheet.liabilitiesAndEquity.totalLiabilitiesAndEquity,
    ) < 0.01;

  return (
    <>
      {!hideHeader && <PageHeader title={t(`title`)} subtitle={t(`subtitle`)} />}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        <div className="rounded-xl bg-white p-6 text-gray-800 shadow-md">
          {/* Header */}
          <p className="mb-6 text-sm text-gray-500">
            {t('date', {
              date: new Date().toLocaleDateString(),
            })}
          </p>

          {/* Main grid */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Assets */}
            <div>
              <div className="mb-4 flex flex-row items-center gap-2">
                <FaBuilding />
                <h3 className="inline-block text-xl font-semibold">{t(`assets.title`)}</h3>
              </div>

              {/* Bank Accounts */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaCreditCard />
                  <h4 className="font-medium">{t(`assets.bankAccounts`)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  {balanceSheet.assets.bankAccounts.accounts.map((account, i) => (
                    <div className="flex justify-between" key={i}>
                      <span>{account.accountName}</span>
                      <span>{formatEuroCentsWithSign(account.balance, locale)}</span>
                    </div>
                  ))}

                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span>{t(`total`)}</span>
                    <span>
                      {formatEuroCentsWithSign(
                        balanceSheet.assets.bankAccounts.totalBalance,
                        locale,
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Receivables */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <HiUserGroup />
                  <h4 className="font-medium">{t(`assets.receivables.title`)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t(`assets.receivables.negativePersonalBalances`)}</span>
                    <span>
                      {formatEuroCents(
                        balanceSheet.assets.receivables.negativePersonalBalance,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t(`assets.receivables.contributionReceivables`)}</span>
                    <span>
                      {formatEuroCents(
                        balanceSheet.assets.receivables.contributionReceivables,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span>{t(`total`)}</span>
                    <span>
                      {formatEuroCents(balanceSheet.assets.receivables.totalReceivables, locale)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Unassigned */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaReceipt />
                  <h4 className="font-medium">{t(`assets.unassigned.title`)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t(`assets.unassigned.unassignedExpenses`)}</span>
                    <span>
                      {formatEuroCents(balanceSheet.assets.unassigned.unassignedExpenses, locale)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span>{t(`total`)}</span>
                    <span>
                      {formatEuroCents(balanceSheet.assets.unassigned.totalUnassigned, locale)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Assets */}
              <div className="flex justify-between rounded-lg border border-blue-200 bg-blue-50 p-4 font-semibold">
                <span>{t(`assets.totalAssets`)}</span>
                <span>{formatEuroCentsWithSign(balanceSheet.assets.totalAssets, locale)}</span>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div>
              <div className="mb-4 flex flex-row items-center gap-2">
                <FaSackDollar />
                <h3 className="inline-block text-xl font-semibold">
                  {t(`liabilitiesEquity.title`)}
                </h3>
              </div>

              {/* Equity */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaChartLine />
                  <h4 className="font-medium">{t(`liabilitiesEquity.equity.title`)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t(`liabilitiesEquity.equity.initialEquity`)}</span>
                    <span>
                      {formatEuroCentsWithSign(
                        balanceSheet.liabilitiesAndEquity.equity.initialEquity,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t(`liabilitiesEquity.equity.profitSinceStart`)}</span>
                    <span>
                      {formatEuroCentsWithSign(
                        balanceSheet.liabilitiesAndEquity.equity.profitSinceStart,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span>{t(`total`)}</span>
                    <span>
                      {formatEuroCentsWithSign(
                        balanceSheet.liabilitiesAndEquity.equity.totalEquity,
                        locale,
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Liabilities */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaWallet />
                  <h4 className="font-medium">{t(`liabilitiesEquity.liabilities.title`)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t(`liabilitiesEquity.liabilities.positivePersonalBalances`)}</span>
                    <span>
                      {formatEuroCents(
                        balanceSheet.liabilitiesAndEquity.liabilities.positivePersonalBalance,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t(`liabilitiesEquity.liabilities.contributionOverpayments`)}</span>
                    <span>
                      {formatEuroCents(
                        balanceSheet.liabilitiesAndEquity.liabilities.contributionOverPayments,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span>{t(`total`)}</span>
                    <span>
                      <span>
                        {formatEuroCents(
                          balanceSheet.liabilitiesAndEquity.liabilities.totalLiabilities,
                          locale,
                        )}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Unassigned */}
              <div className="mb-4 rounded-lg border p-4">
                <div className="mb-3 flex flex-row items-center gap-2">
                  <FaReceipt />
                  <h4 className="font-medium">{t(`liabilitiesEquity.unassigned.title`)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t(`liabilitiesEquity.unassigned.unassignedIncome`)}</span>
                    <span>
                      {formatEuroCents(
                        balanceSheet.liabilitiesAndEquity.unassigned.unassignedIncome,
                        locale,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-medium">
                    <span>{t(`total`)}</span>
                    <span>
                      {formatEuroCents(
                        balanceSheet.liabilitiesAndEquity.unassigned.totalUnassigned,
                        locale,
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Liabilities & Equity */}
              <div className="flex justify-between rounded-lg border border-blue-200 bg-blue-50 p-4 font-semibold">
                <span>{t(`liabilitiesEquity.totalLiabilities`)}</span>
                <span>
                  {formatEuroCentsWithSign(
                    balanceSheet.liabilitiesAndEquity.totalLiabilitiesAndEquity,
                    locale,
                  )}
                </span>
              </div>

              {/* Balance check */}
              <div
                className={`mt-4 items-center rounded-lg p-3 text-center text-sm font-medium ${
                  isBalanced ? 'bg-green-100 text-success' : 'bg-red-100 text-danger'
                }`}>
                {isBalanced ? (
                  <div className="flex items-center justify-center gap-2">
                    <FaCheck />
                    {t('isBalanced')}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <FaTriangleExclamation />
                    {t('isNotBalanced', {
                      difference: formatEuroCentsWithSign(
                        Math.abs(
                          balanceSheet.assets.totalAssets -
                            balanceSheet.liabilitiesAndEquity.totalLiabilitiesAndEquity,
                        ),
                        locale,
                      ),
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-8 rounded-lg bg-gray-50 p-4 text-sm">
            <div className="mb-3 flex flex-row items-center gap-2">
              <FaChartSimple />
              <h4 className="font-medium">{t(`summary.title`)}</h4>
            </div>
            <p>{t('summary.description')}</p>
          </div>
        </div>
      </div>
    </>
  );
}
